import { FC, useCallback, useEffect, useLayoutEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../assets/hooks/hooks";
import Loader from "../../../assets/components/Loader/Loader";
import PageTitle from "../../../assets/components/PageTitle/PageTitle";
import { Page } from "../../../assets/components/PaginationBar/PaginationBar";
import { SearchBarDataHandler } from "../../../assets/components/SearchBar/SearchBar";
import { Loading, uiSlicedActions } from "../../../redux/ui-slice";
import { MigratedLicenseKeysData } from "./model";
import {
    useDeleteMigratedLicensesMutation,
    useGetMigratedLicensesListQuery,
    useGetProductsListQuery,
    useUploadCSVMigratedLicenseMutation,
} from "../../../redux/RTKQuery/api";
import LicenseKeysMigratedTable from "./LicenseKeysMigratedTable/LicenseKeysMigratedTable";
import LicenseKeysMigratedTableHeader from "./LicenseKeysMigratedTable/LicenseKeysMigratedTableHeader";
import { toast } from "react-toastify";
import LicenseKeysMigratedSearchBar from "./LicenseKeysMigratedTable/LicenseKeysMigratedSearchBar";
import { Products } from "../../../models/models";

const LicenseKeysMigrated: FC = () => {
    const dispatch = useAppDispatch();
    const loading: Loading = useAppSelector((state) => state.ui.loading);
    const pageSize: number = 30;
    const [dataTable, setData] = useState<MigratedLicenseKeysData[]>([]);
    const [pageNumber, setCurrentPage] = useState<Page>(1);
    const [products, setDataProducts] = useState<Products>([]);
    const [quiresData, setQuires] = useState<SearchBarDataHandler>({});
    const [totalLicenses, setTotalLicenses] = useState<number>(0);

    const [
        ,
        {
            isLoading: isLoadingUploadCSV,
            isError: isErrorUploadCSV,
            isSuccess: isSuccessUploadCSV,
            reset: resetUploadCSV,
        },
    ] = useUploadCSVMigratedLicenseMutation({
        fixedCacheKey: "upload_migrated_license",
    });

    const [
        ,
        {
            isLoading: isLoadingDelete,
            isError: isErrorDelete,
            isSuccess: isSuccessDelete,
            reset: resetDelete,
        },
    ] = useDeleteMigratedLicensesMutation({
        fixedCacheKey: "delete_migrated_license",
    });

    const {
        data: dataMigratedLicenses,
        isLoading: isLoadingMigratedLicenses,
        isSuccess: isSuccessMigratedLicenses,
        isError: isErrorMigratedLicenses,
    } = useGetMigratedLicensesListQuery(
        {
            pageNumber,
            pageSize,
            key: quiresData.searchInput,
            orderId: quiresData.searchByOrderId,
            productId: quiresData.productId,
            isMigrated: quiresData.isMigrated,
            sortNextRebillDate: quiresData.sortNextRebillDate,
            nextRebillStartDate: quiresData.nextRebillStartDate,
            nextRebillEndDate: quiresData.nextRebillEndDate,
        },
        { refetchOnFocus: true }
    );

    const {
        data: dataProducts,
        isSuccess: isSuccessProducts,
        isError: isErrorProducts,
        isLoading: isLoadingProducts,
    } = useGetProductsListQuery(
        {},
        {
            refetchOnFocus: true,
        }
    );

    useEffect(() => {
        setCurrentPage(1);
    }, [dataMigratedLicenses?.filteredLicensesCount]);

    const handlerDeleteMigratedLicenses = useCallback(() => {
        if (isSuccessDelete) {
            toast.success("License key deleted successfully");
            resetDelete();
        }
        if (isErrorDelete) {
            toast.error("Error deleting license key");
        }
    }, [isErrorDelete, isSuccessDelete, resetDelete]);

    const handleProductsData = useCallback(() => {
        if (isSuccessProducts && products) {
            setDataProducts(dataProducts.products);
        }
        if (isErrorProducts) {
            toast.error("Error loading products");
        }
    }, [isSuccessProducts, products, isErrorProducts, dataProducts]);

    const handleLicensesData = useCallback(() => {
        if (isSuccessMigratedLicenses && dataMigratedLicenses) {
            setData(dataMigratedLicenses.licenses);
            if (dataMigratedLicenses.filteredLicensesCount !== undefined) {
                setTotalLicenses(dataMigratedLicenses.filteredLicensesCount);
            } else {
                setTotalLicenses(dataMigratedLicenses.totalLicenses);
            }
        }
        if (isErrorMigratedLicenses) {
            toast.error("Error loading license keys");
        }
    }, [
        dataMigratedLicenses,
        isErrorMigratedLicenses,
        isSuccessMigratedLicenses,
    ]);

    const handleUploadCSV = useCallback(() => {
        if (isSuccessUploadCSV) {
            toast.success("License key(s) uploaded successfully!");
            resetUploadCSV();
        }
        if (isErrorUploadCSV) {
            toast.error("Error uploading license key(s)");
            resetUploadCSV();
        }
    }, [isErrorUploadCSV, isSuccessUploadCSV, resetUploadCSV]);

    const handleLoadingState = useCallback(() => {
        const loadingGlobal =
            isLoadingMigratedLicenses ||
            isLoadingProducts ||
            isLoadingDelete ||
            isLoadingUploadCSV;
        dispatch(
            uiSlicedActions.setLoading(loadingGlobal ? "pending" : "hidden")
        );
    }, [
        isLoadingMigratedLicenses,
        isLoadingProducts,
        isLoadingDelete,
        isLoadingUploadCSV,
        dispatch,
    ]);

    // Handle data fetching and state updates
    useLayoutEffect(() => {
        handlerDeleteMigratedLicenses();
        handleProductsData();
        handleLicensesData();
        handleLoadingState();
        handleUploadCSV();
    }, [
        handleLicensesData,
        handleLoadingState,
        handleProductsData,
        handlerDeleteMigratedLicenses,
        handleUploadCSV,
    ]);
    return (
        <div className="page_with_table">
            <Loader loading={loading} />

            <PageTitle text="Migrated License Keys" />

            <LicenseKeysMigratedSearchBar
                dataHandler={setQuires}
                products={products}
            />

            <div className="table_scroll_wrapper">
                <LicenseKeysMigratedTableHeader
                    pageNumber={pageNumber}
                    sizePage={pageSize}
                    dataTable={dataTable}
                    totalLicenses={totalLicenses}
                    setCurrentPage={setCurrentPage}
                    currentSizePage={pageSize}
                />

                <LicenseKeysMigratedTable
                    dataTable={dataTable}
                    onSort={setQuires}
                />
            </div>
        </div>
    );
};

export default LicenseKeysMigrated;
